import React from "react";
import { FormProvider, useForm, } from "react-hook-form";
import styled from "styled-components";
import { cloneDeep } from "lodash";
import { DevTool } from "@hookform/devtools";
import { includeStyledSystem, sx } from "../../util/styledSystem";
export const DEBUGGER_KEY = "RHFDevTool";
const StyledForm = styled.form `
  ${includeStyledSystem()}
  ${sx}
`;
/**
 * A form component that integrates with react-hook-form for advanced form state management. The form debugger can be enabled by setting
 * the `RHFDevTool` in localStorage.
 *
 * - Debugger documentation: https://react-hook-form.com/devtools
 *
 *
 * @template T - The type of form values, defaults to empty object
 * @component
 *
 * @example
 * ```tsx
 * <Form onSubmit={(values) => console.log(values)}>
 *   <FormInput label="First Name" name="firstName" />
 * </Form>
 * ```
 *
 * @param props - Component props
 * @param props.children - Form field components to be rendered
 * @param props.defaultValues - Initial values for the form fields
 * @param props.id - Unique identifier for the form element
 * @param props.onSubmit - Handler called with form values when submission is successful
 * @param props.onError - Handler called when form validation fails
 * @param props.sx - Styled-system CSS properties for custom styling
 * @param props.validate - Custom validation resolver function
 * @param props.mode - When to trigger validation ("onChange" | "onBlur" | "onSubmit" | "onTouched" | "all")
 * @param props.reValidateMode - When to trigger revalidation after submission
 *
 * @returns A form component wrapped with FormProvider for context access
 */
export const Form = ({ children, defaultValues, id, onSubmit, onError, sx = {}, validate, mode = "onSubmit", // validate initially on submit
reValidateMode = "onChange", // after submit, revalidate on change
 }) => {
    const formMethods = useForm({
        defaultValues,
        mode,
        resolver: validate,
        reValidateMode,
        shouldUnregister: true, // be uncontrolled
    });
    // Enable the form debugger if the key is set in localStorage
    const RHFDevTool = Boolean(localStorage.getItem(DEBUGGER_KEY));
    return (React.createElement(FormProvider, Object.assign({}, formMethods),
        React.createElement(StyledForm, { "data-testid": "FormElement", id: id, onSubmit: (event) => {
                event === null || event === void 0 ? void 0 : event.preventDefault();
                event === null || event === void 0 ? void 0 : event.stopPropagation();
                return formMethods.handleSubmit(
                // We clone values to avoid accidental data manipulation through an object reference.
                (values) => onSubmit(cloneDeep(values), formMethods.setError, formMethods), onError)(event);
            }, sx: sx },
            children,
            RHFDevTool && React.createElement(DevTool, { control: formMethods.control }))));
};
